import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {createUser, login, retrieveUser} from "../clients/UserClient";
import Profile from "../components/Profile";
import Icon from "../components/ui/Icon";
import usePageTheme from "../utils/usePageTheme";

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

const errorText = (error) => error?.response?.data?.message || error?.message || "Something went wrong";

const AuthCard = () => {
    usePageTheme("park");
    const navigate = useNavigate();
    const [mode, setMode] = useState("login");
    const [username, setUsername] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [repeatedPassword, setRepeatedPassword] = useState("");
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const validateSignup = () => {
        if (!username.trim() || username.trim().length > 25) {
            return "Your username must be between 1 and 25 characters.";
        }
        if (!emailRegex.test(email)) {
            return "Please enter a valid e-mail address.";
        }
        if (!password.trim()) {
            return "Please choose a password.";
        }
        if (password !== repeatedPassword) {
            return "The passwords do not match.";
        }
        return null;
    };

    const submit = async (event) => {
        event.preventDefault();
        setError(null);
        if (mode === "signup") {
            const problem = validateSignup();
            if (problem) {
                setError(problem);
                return;
            }
        } else if (!username.trim() || !password) {
            setError("Please fill in your username and password.");
            return;
        }

        setBusy(true);
        try {
            if (mode === "signup") {
                await createUser(username.trim(), email.trim(), password);
            }
            await login(username.trim(), password);
            // Reload so the live connection picks up the new login
            navigate(0);
        } catch (e) {
            setError(errorText(e));
            setBusy(false);
        }
    };

    const switchMode = (next) => {
        setMode(next);
        setError(null);
    };

    return (
        <div className="page">
            <div className="card auth-card">
                <div className="auth-card__hero">
                    <img src="/logo.png" alt="Chess Corner logo"/>
                    <h1 style={{fontSize: "1.7rem"}}>{mode === "login" ? "Welcome back" : "Join Chess Corner"}</h1>
                    <p>{mode === "login"
                        ? "Log in to keep your rating, friends and game history."
                        : "Create an account to get a rating, add friends and review your games."}</p>
                </div>
                <div className="auth-tabs">
                    <div className="segmented" role="tablist">
                        <button type="button" role="tab" aria-selected={mode === "login"}
                                className={mode === "login" ? "active" : ""} onClick={() => switchMode("login")}>Log in</button>
                        <button type="button" role="tab" aria-selected={mode === "signup"}
                                className={mode === "signup" ? "active" : ""} onClick={() => switchMode("signup")}>Create account</button>
                    </div>
                </div>
                <form onSubmit={submit} noValidate>
                    <div className="field">
                        <label htmlFor="auth-username">Username</label>
                        <input id="auth-username" className="input" autoComplete="username" value={username}
                               onChange={(e) => setUsername(e.target.value)} autoFocus/>
                    </div>
                    {mode === "signup" && (
                        <div className="field">
                            <label htmlFor="auth-email">E-mail</label>
                            <input id="auth-email" type="email" className="input" autoComplete="email" value={email}
                                   onChange={(e) => setEmail(e.target.value)}/>
                        </div>
                    )}
                    <div className="field">
                        <label htmlFor="auth-password">Password</label>
                        <input id="auth-password" type="password" className="input"
                               autoComplete={mode === "login" ? "current-password" : "new-password"}
                               value={password} onChange={(e) => setPassword(e.target.value)}/>
                    </div>
                    {mode === "signup" && (
                        <div className="field">
                            <label htmlFor="auth-password-repeat">Repeat password</label>
                            <input id="auth-password-repeat" type="password" className="input" autoComplete="new-password"
                                   value={repeatedPassword} onChange={(e) => setRepeatedPassword(e.target.value)}/>
                        </div>
                    )}
                    {error && <div className="form-error" role="alert">{error}</div>}
                    <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={busy}>
                        {busy ? <span className="spinner spinner--sm spinner--light"/> : <Icon name={mode === "login" ? "login" : "sprout"}/>}
                        {mode === "login" ? "Log in" : "Create account"}
                    </button>
                    <p className="small muted" style={{textAlign: "center"}}>
                        You can always play as a guest. An account is only needed for ratings and friends.
                    </p>
                </form>
            </div>
        </div>
    );
};

const MyProfile = () => {
    const [userData, setUserData] = useState({loading: true})

    useEffect(() => {
        retrieveUser()
            .then((data) => setUserData({loading: false, loggedIn: true, ...data}))
            .catch(() => setUserData({loading: false, loggedIn: false}))
    }, []);

    if (userData.loading) {
        return (
            <div className="page" style={{display: "flex", justifyContent: "center", paddingTop: "4rem"}}>
                <span className="spinner"/>
            </div>
        );
    }

    return userData.loggedIn
        ? <Profile username={userData.username} ownProfile={true}/>
        : <AuthCard/>;
}

export default MyProfile;
